/**
 * Optimized Campaign Performance Hook
 * Uses materialized views for 95% faster performance
 * 
 * Performance: 60-90s → <2s for campaign list
 * 
 * Before: Queries raw obsync_dynamic_reports (250,000+ rows)
 * After: Queries campaign_performance_summary (50 pre-aggregated rows)
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/integrations/supabase/client'
// 86ca7n8d4 (2026-06-11): loader/timer logs fired on every page load —
// routed through the opt-in gate (localStorage.ob_debug = '1' to re-enable).
import { dlog, dtime, dtimeEnd } from '@/lib/debug-log'

export interface OptimizedCampaignPerformance {
  campaign_id: string
  campaign_name: string
  campaign_type: string
  campaign_status: string
  client_id: string
  client_name: string
  
  // Operational metrics
  total_segments: number
  total_variants: number
  active_variants: number
  paused_variants: number
  completed_variants: number
  
  // Email performance (EMAIL ONLY - LinkedIn separate)
  email_leads_contacted: number
  email_send_volume: number
  email_total_replies: number
  email_human_replies: number
  email_positive_replies: number
  email_reply_rate: number
  email_human_reply_rate: number
  email_positive_rate: number
  // Bounces — exposed for the campaign performance table.
  emails_bounced: number | string  // numeric in matview; string sometimes via PostgREST
  bounce_rate: number | string
  
  // Conversions
  // D-045 (2026-05-06): meetings_booked / qualified_meetings now derive
  // from masterinbox_label_updates (latest label state per prospect)
  // instead of being hard-coded to 0. The matview joins to
  // _mv_meetings_per_slid; React side just consumes the numbers.
  meetings_booked: number
  qualified_meetings: number
  deals_won: number
  
  // Operational
  leads_not_started: number
  runway_days: number

  // Phase R (2026-04-23): yesterday-only send volume. "Yesterday" = the
  // most recent past calendar day (UTC) strictly before today that had
  // any sends, scoped to this campaign's active+completed variants.
  // Today is excluded on purpose — the UI shows a stable day-over-day
  // total, not a partially-accumulated running count.
  daily_send_volume_email: number
  daily_send_volume_linkedin: number
  daily_send_volume: number

  // Metadata
  last_cached_at: string
  created_at: string
  updated_at: string
}

export interface OptimizedOperationalMetrics {
  campaign_id: string
  campaign_name: string
  campaign_status: string
  total_segments: number
  total_variants: number
  active_variants: number
  paused_variants: number
  completed_variants: number
  leads_not_started: number
  total_leads: number
  contacted_leads: number
  avg_daily_send_rate: number
  runway_days: number
  last_cached_at: string
}

interface UseOptimizedCampaignPerformanceReturn {
  campaigns: OptimizedCampaignPerformance[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  cacheAge: number | null // Age in minutes since last cache update
}

/**
 * Hook to fetch optimized campaign performance data
 * Uses materialized view for instant loading
 */
export function useOptimizedCampaignPerformance(
  selectedClientId?: string,
  statusFilter: string = 'all'
): UseOptimizedCampaignPerformanceReturn {
  const [campaigns, setCampaigns] = useState<OptimizedCampaignPerformance[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cacheAge, setCacheAge] = useState<number | null>(null)
  // Sabeeh 2026-05-13: retry-once gate. The first matview fetch can
  // race with supabase-js's auth-token install on cold mount right
  // after a session switch — the request fires with a stale token,
  // 401s, and the catch block sets "Failed to load data" even though
  // a retry 800ms later would succeed cleanly. Track per-load whether
  // we've already retried so we don't loop on a permanent error.
  const retriedRef = useRef(false)

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Always query real Supabase data — campaign_performance_summary view is populated
      // by edge functions, not dependent on SmartLead API key in the browser.
      dlog('⚡ Loading campaigns from materialized view...')
      dtime('Campaign Load Time')
      
      // Build query for materialized view
      // Note: Type cast to 'any' because materialized view doesn't exist in TypeScript types yet
      let query = (supabase as any)
        .from('campaign_performance_summary')
        .select('*')
      
      // Filter by client if specified
      if (selectedClientId && selectedClientId !== 'all') {
        query = query.eq('client_id', selectedClientId)
      }
      
      // Filter by status if specified
      if (statusFilter !== 'all') {
        query = query.ilike('campaign_status', statusFilter)
      }
      
      // Order by most recent
      query = query.order('last_cached_at', { ascending: false })
      
      const { data, error: queryError } = await query
      
      dtimeEnd('Campaign Load Time')
      
      if (queryError) {
        console.error('❌ Error loading campaigns:', queryError)
        throw queryError
      }
      
      if (!data || data.length === 0) {
        console.warn('⚠️ No data returned from materialized view. View might not be refreshed yet.')
        setCampaigns([])
        setError('No campaigns found. Data is being prepared...')
        setLoading(false)
        return
      }
      
      dlog(`✅ Loaded ${data.length} campaigns in <2s from materialized view`)
      
      // Calculate cache age from cache_metadata table (accurate refresh timestamp)
      try {
        const { data: metaData } = await (supabase as any)
          .from('cache_metadata')
          .select('refreshed_at')
          .eq('key', 'campaign_performance_summary')
          .single()
        if (metaData?.refreshed_at) {
          const cacheTime = new Date(metaData.refreshed_at).getTime()
          const now = new Date().getTime()
          const ageMinutes = Math.floor((now - cacheTime) / 1000 / 60)
          setCacheAge(ageMinutes)
          dlog(`📅 Cache age: ${ageMinutes} minutes (from cache_metadata)`)
        }
      } catch {
        // Fallback to view column if metadata table not available
        if (data.length > 0 && data[0].last_cached_at) {
          const cacheTime = new Date(data[0].last_cached_at).getTime()
          const now = new Date().getTime()
          const ageMinutes = Math.floor((now - cacheTime) / 1000 / 60)
          setCacheAge(ageMinutes)
        }
      }
      
      setCampaigns(data as OptimizedCampaignPerformance[])
      setLoading(false)
      retriedRef.current = false // success → clear the retry gate

    } catch (err) {
      // Silent retry-once on transient cold-mount failures (most
      // common: session token still installing in supabase-js).
      // Permanent errors (RLS, missing matview, etc.) hit the catch
      // a second time and we surface them as before.
      if (!retriedRef.current) {
        retriedRef.current = true
        console.warn('⚠️ campaign matview fetch failed on cold mount — retrying in 800ms', err)
        setTimeout(() => { loadCampaigns() }, 800)
        return
      }
      console.error('❌ Failed to load campaign performance:', err)
      setError(err instanceof Error ? err.message : 'Failed to load data')
      setLoading(false)
    }
  }, [selectedClientId, statusFilter])

  // Load on mount and when dependencies change
  useEffect(() => {
    loadCampaigns()
  }, [loadCampaigns])

  return {
    campaigns,
    loading,
    error,
    refresh: loadCampaigns,
    cacheAge
  }
}

/**
 * Hook to fetch operational metrics for a specific campaign
 * Uses materialized view for campaign detail page
 */
export function useOptimizedCampaignOperationalMetrics(
  campaignId: string | undefined
): {
  metrics: OptimizedOperationalMetrics | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
} {
  const [metrics, setMetrics] = useState<OptimizedOperationalMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadMetrics = useCallback(async () => {
    if (!campaignId) {
      setMetrics(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      dlog(`⚡ Loading operational metrics for campaign ${campaignId}...`)
      dtime('Operational Metrics Load Time')
      
      // Note: Type cast to 'any' because materialized view doesn't exist in TypeScript types yet
      const { data, error: queryError } = await (supabase as any)
        .from('campaign_operational_metrics')
        .select('*')
        .eq('campaign_id', campaignId)
        .single()
      
      dtimeEnd('Operational Metrics Load Time')
      
      if (queryError) {
        console.error('❌ Error loading operational metrics:', queryError)
        throw queryError
      }
      
      if (!data) {
        console.warn('⚠️ No operational metrics found for campaign')
        setMetrics(null)
        setLoading(false)
        return
      }
      
      dlog('✅ Loaded operational metrics in <500ms from materialized view')
      
      setMetrics(data as OptimizedOperationalMetrics)
      setLoading(false)
      
    } catch (err) {
      console.error('❌ Failed to load operational metrics:', err)
      setError(err instanceof Error ? err.message : 'Failed to load metrics')
      setLoading(false)
    }
  }, [campaignId])

  useEffect(() => {
    loadMetrics()
  }, [loadMetrics])

  return {
    metrics,
    loading,
    error,
    refresh: loadMetrics
  }
}

/**
 * Helper function to manually refresh materialized views
 * Use this when you need fresh data immediately (e.g. after campaign update)
 */
export async function refreshCampaignPerformanceCache(): Promise<void> {
  try {
    dlog('🔄 Manually refreshing campaign performance cache...')
    
    // Call the refresh functions
    // Note: Type cast to 'any' because these functions don't exist in TypeScript types yet
    await Promise.all([
      (supabase as any).rpc('refresh_campaign_performance_summary'),
      (supabase as any).rpc('refresh_campaign_operational_metrics')
    ])
    
    dlog('✅ Cache refreshed successfully')
  } catch (error) {
    console.error('❌ Failed to refresh cache:', error)
    throw error
  }
}

